// semeia-disable-reexport
import type Patient from "parog-web/models/patient";
import type DmpRetrievalStartedJournalEvent from "parog-web/models/dmp-retrieval-started-journal-event";
import Performer from "parog-web/models/performer";
import Prescriptor from "parog-web/models/prescriptor";
import Specialty from "parog-web/models/specialty";
import {
  dayDiffBetweenDates,
  hoursDiffBetweenDates
} from "parog-web/utils/time";

export function hasNoDmpRetrievalSuccessfulSinceYesterday(
  patient: Patient
): boolean {
  const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
  yesterday.setHours(0, 0, 0, 0);

  const latestDmpRetrievalSuccessfulJE =
    patient.dmpRetrievalSuccessfulJournalEvent;

  return (
    !latestDmpRetrievalSuccessfulJE ||
    latestDmpRetrievalSuccessfulJE?.occurredAt < yesterday
  );
}

function dmpRetrievalStartedJesPerCpsId(
  jes: DmpRetrievalStartedJournalEvent[],
  cpsId: string
): DmpRetrievalStartedJournalEvent[] {
  return jes.filter(je => je.cpsId === cpsId);
}

export function numberTriesForCurrentHourPerCpsId(
  jes: DmpRetrievalStartedJournalEvent[],
  cpsId: string
): number {
  return dmpRetrievalStartedJesPerCpsId(jes, cpsId).filter(
    je => hoursDiffBetweenDates(new Date(), je.occurredAt) == 0
  ).length;
}

export function numberTriesForLastHourPerCpsId(
  jes: DmpRetrievalStartedJournalEvent[],
  cpsId: string
): number {
  return dmpRetrievalStartedJesPerCpsId(jes, cpsId).filter(
    je => hoursDiffBetweenDates(new Date(), je.occurredAt) < 1
  ).length;
}

export function numberTriesForLastDayPerCpsId(
  jes: DmpRetrievalStartedJournalEvent[],
  cpsId: string
): number {
  return dmpRetrievalStartedJesPerCpsId(jes, cpsId).filter(
    je => dayDiffBetweenDates(new Date(), je.occurredAt) == 0
  ).length;
}

export function numberTriesForLastTwentyFourHoursPerCpsId(
  jes: DmpRetrievalStartedJournalEvent[],
  cpsId: string
): number {
  return dmpRetrievalStartedJesPerCpsId(jes, cpsId).filter(
    je => hoursDiffBetweenDates(new Date(), je.occurredAt) < 24
  ).length;
}

export function prescriptorRetrieve(authorObject: any): Prescriptor {
  const assignedPerson = authorObject?.assignedAuthor?.assignedPerson?.name;
  let firstName;
  let lastName;

  if (assignedPerson) {
    firstName = assignedPerson?.given?._text;
    lastName = assignedPerson?.family?._text;
  }

  const assignedAuthor = authorObject?.assignedAuthor;
  let identifier;
  if (assignedAuthor?.id?._attributes?.root == "1.2.250.1.71.4.2.1") {
    identifier = assignedAuthor?.id._attributes.extension;
  }

  const code = assignedAuthor?.code?._attributes;
  const specialty = new Specialty({
    codeSystemName: code?.codeSystemName,
    codeSystem: code?.codeSystem,
    code: code?.code,
    displayName: code?.displayName
  });

  return new Prescriptor({ firstName, lastName, identifier, specialty });
}

export function performerRetrieve(performerObject: any): Performer {
  const assignedPerson = performerObject?.assignedEntity?.assignedPerson?.name;
  let firstName;
  let lastName;
  if (assignedPerson) {
    firstName = assignedPerson?.given?._text;
    lastName = assignedPerson?.family?._text;
  }

  const code = performerObject?.assignedEntity?.code?._attributes;
  const specialty = new Specialty({
    codeSystemName: code?.codeSystemName,
    codeSystem: code?.codeSystem,
    code: code?.code,
    displayName: code?.displayName
  });

  const representedOrganization =
    performerObject?.assignedEntity?.representedOrganization;
  const organizationName = representedOrganization?.name?._text;
  let organizationIdentifier: string | undefined;
  if (representedOrganization?.id?._attributes?.root == "1.2.250.1.71.4.2.2") {
    organizationIdentifier =
      representedOrganization?.id?._attributes?.extension;
  }

  return new Performer({
    name: {
      firstName,
      lastName
    },
    specialty,
    organization: {
      name: organizationName,
      identifier: organizationIdentifier
    }
  });
}
